import { CUSTOM_ELEMENTS_SCHEMA, NgModule, NO_ERRORS_SCHEMA, LOCALE_ID, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, HttpClient } from '@angular/common/http';
import { DatePipe, registerLocaleData } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  DateAdapter,
  ErrorStateMatcher,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  ShowOnDirtyErrorStateMatcher
} from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { GoogleLoginProvider, SocialAuthServiceConfig } from '@abacritt/angularx-social-login';
import { ToastrModule } from 'ngx-toastr';
import { CalendarModule, DateAdapter as CalendarDateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';

import { LanguageService } from './shared/services/language.service';
import localeUa from '@angular/common/locales/uk';
import localeEn from '@angular/common/locales/en';
import { uk } from 'date-fns/locale/uk';
import { enUS } from 'date-fns/locale/en-US';

import { CustomDateAdapter } from './shared/adapters/custom-date-adapter';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { SharedModule } from './shared/shared.module';
import { LoginInterceptor } from './shared/interceptors/login.interceptor';
import { LoaderInterceptor } from './shared/interceptors/loader.interceptor';

import { APP_LANGUAGE } from './shared/enums';

import { LanguageInterceptor } from './shared/interceptors/language.interceptor';
import {
  YoutubeVideoModalComponent
} from './shared/components/modals/youtube-video-modal/youtube-video-modal.component';
import {
  GoogleUnlinkAccountModalComponent
} from './shared/components/modals/google-unlink-account-modal/google-unlink-account-modal.component';

import {
  ChangePasswordModalComponent
} from './shared/components/modals/change-password-modal/change-password-modal.component';
import { LandingLayoutComponent } from './landing/landing-layout.component';
import { AuthLayoutComponent } from './auth/auth-layout.component';
import { WorkspaceLayoutComponent } from './workspace/workspace-layout.component';
import { CompanyLayoutComponent } from './company/company-layout.component';

import { DashboardLayoutComponent } from './dashboard/dashboard-layout.component';
import { HowToGuidePageComponent } from './general/how-to-guide-page/how-to-guide-page.component';
import { ContactsPageComponent } from './general/contacts-page/contacts-page.component';
import { TermsPageComponent } from './general/terms-page/terms-page.component';
import { CookiesPolicyPageComponent } from './general/cookies-policy-page/cookies-policy-page.component';
import { PrivacyPolicyPageComponent } from './general/privacy-policy-page/privacy-policy-page.component';
import { UserProfileEditPageComponent } from './general/user-profile-edit-page/user-profile-edit-page.component';

const DATEPICKER_FORMATS = {
  parse: {
    dateInput: 'dd.MM.yyyy'
  },
  display: {
    dateInput: 'dd.MM.yyyy',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'PP',
    monthYearA11yLabel: 'MMMM yyyy'
  }
};

export const myCustomTooltipDefaults: MatTooltipDefaultOptions = {
  showDelay: 0,
  hideDelay: 0,
  touchendHideDelay: 1500,
  position: 'above',
  tooltipClass: 'custom-tooltip'
};

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

registerLocaleData(localeUa, 'uk');
registerLocaleData(localeEn, 'en');

@NgModule({
  declarations: [
    AppComponent,
    LandingLayoutComponent,
    AuthLayoutComponent,
    WorkspaceLayoutComponent,
    CompanyLayoutComponent,
    DashboardLayoutComponent,
    HowToGuidePageComponent,
    ContactsPageComponent,
    TermsPageComponent,
    CookiesPolicyPageComponent,
    PrivacyPolicyPageComponent,
    YoutubeVideoModalComponent,
    UserProfileEditPageComponent,
    GoogleUnlinkAccountModalComponent,
    ChangePasswordModalComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    SharedModule,
    ToastrModule.forRoot({
      positionClass: 'toast-bottom-right',
      closeButton: true,
      timeOut: 5000,
      preventDuplicates: true
    }),
    CalendarModule.forRoot({
      provide: CalendarDateAdapter,
      useFactory: adapterFactory
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'en'
    })
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: LoginInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    {
      provide: 'SocialAuthServiceConfig',
      useValue: {
        autoLogin: false,
        providers: [
          {
            id: GoogleLoginProvider.PROVIDER_ID,
            provider: new GoogleLoginProvider('852566226006-0sg8a3b91u0hc3trs8lpa7mnsrskhv4u.apps.googleusercontent.com', { oneTapEnabled: false })
          }
        ]
      } as SocialAuthServiceConfig
    },
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: LanguageService) => languageService.getCurrentLanguage()
    },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { autoFocus: 'dialog' }
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic', appearance: 'outline' }
    },
    { provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: myCustomTooltipDefaults },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: false
        }
      }
    }),
    DatePipe,
    { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMATS },
    {
      provide: MAT_DATE_LOCALE,
      useFactory: (locale: APP_LANGUAGE) => locale === APP_LANGUAGE.EN ? enUS : uk,
      deps: [LOCALE_ID]
    },
    { provide: DateAdapter, useClass: CustomDateAdapter, deps: [MAT_DATE_LOCALE] },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
}
